// Player-side preferences for the controls and overlays. They never reach the simulation.

export interface Settings {
  /** the move pad sits at a fixed spot instead of where the thumb lands */
  movePadLocked: boolean;
  /** the aim pad sits at a fixed spot instead of where the thumb lands */
  aimPadLocked: boolean;
  /** preview of how the held weapon fires, along the aim (touch) or towards the mouse (desktop) */
  aimLine: boolean;
  /** Fit the entire arena instead of following the player on small screens. */
  overview?: boolean;
  chessSounds?: boolean;
  arenaSounds?: boolean;
}

export const DEFAULT_SETTINGS: Settings = { movePadLocked: false, aimPadLocked: false, aimLine: true, overview: false, chessSounds: true, arenaSounds: true };

/** The subset of Storage we use, so tests can pass a plain object. */
export interface SettingsStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const SETTINGS_KEY = 'chessshooter.settings';

function browserStore(): SettingsStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null; // blocked storage throws on access in some private modes
  }
}

/** Reads the settings; anything missing or malformed falls back to the defaults. */
export function loadSettings(store: SettingsStore | null = browserStore()): Settings {
  const result = { ...DEFAULT_SETTINGS };
  if (!store) return result;
  try {
    const raw = store.getItem(SETTINGS_KEY);
    if (!raw) return result;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return result;
    for (const key of Object.keys(DEFAULT_SETTINGS) as (keyof Settings)[]) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'boolean') result[key] = value;
    }
  } catch {
    // unreadable storage: keep the defaults
  }
  return result;
}

export function saveSettings(settings: Settings, store: SettingsStore | null = browserStore()): void {
  if (!store) return;
  try {
    store.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // full or blocked storage: the setting still applies for this session
  }
}
